import React, { useEffect, useState, createElement } from 'react';
import { ArrowRightIcon, CheckIcon, ChevronDownIcon, ChevronUpIcon, TargetIcon, UsersIcon, LayoutIcon, FileTextIcon, CameraIcon, PaletteIcon, TypeIcon, MailIcon, BookOpenIcon, SearchIcon, LinkIcon, MonitorIcon, SmartphoneIcon, ZapIcon, ServerIcon, ShieldCheckIcon, SettingsIcon } from 'lucide-react';
import { FinalCtaSection } from '../components/FinalCtaSection';
export function WebsiteLatenMakenChecklistPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Website laten maken checklist | Noord';
    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement;
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = 'Complete checklist voor het laten maken van een website. Van domeinnaam en hosting tot content en SEO. Vergeet niets met deze handige checklist van Noord Digital.';
    // Open Graph meta tags
    const ogTags: Record<string, string> = {
      'og:title': 'Website laten maken checklist | Noord',
      'og:description': 'Complete checklist voor het laten maken van een website. Van domeinnaam en hosting tot content en SEO.',
      'og:type': 'article',
      'og:url': 'https://noorddigital.nl/website-laten-maken-checklist',
      'og:site_name': 'Noord Digital',
      'og:locale': 'nl_NL'
    };
    Object.entries(ogTags).forEach(([property, content]) => {
      let tag = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.content = content;
    });
  }, []);
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };
  const faqs = [{
    q: 'Hoe lang duurt het om een website te laten maken?',
    a: "Gemiddeld duurt het 2 tot 4 weken om een professionele website te bouwen. Dit hangt af van de complexiteit, het aantal pagina's en hoe snel je feedback en content aanlevert."
  }, {
    q: 'Kan ik zelf de teksten schrijven?',
    a: 'Ja, dat kan zeker. Wij geven je tips en richtlijnen voor effectieve webteksten. Heb je liever dat wij de teksten schrijven? Dan verzorgen wij professionele copywriting die aansluit bij je doelgroep en SEO.'
  }, {
    q: 'Wat moet er op een homepage staan?',
    a: 'Een goede homepage bevat: een duidelijke kop die vertelt wat je doet, je belangrijkste diensten, social proof (reviews of keurmerken), een duidelijke call-to-action en contactmogelijkheden. De homepage is je digitale visitekaartje.'
  }];
  return <main className="pt-16 lg:pt-[72px]" data-id="element-2140" data-mp="24cbilzo57vdv">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 bg-navy-900 overflow-hidden" data-id="element-2141" data-mp="24b0fm22iurnt">
        <div className="absolute inset-0 pointer-events-none" data-id="element-2142" data-mp="3n1fv2yyansmf">
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl bg-accent" data-id="element-2143" data-mp="3rzpob40gkj5e" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-10 blur-3xl bg-accent-light" data-id="element-2144" data-mp="3vyq4eerjx7op" />
          <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '32px 32px'
        }} data-id="element-2145" data-mp="4rvq3f7u66po" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2146" data-mp="jjo3vmrzfcpa">
          {/* Breadcrumb */}
          <nav className="flex items-center justify-center gap-2 text-sm mb-8 animate-fade-in-up" data-id="element-2147" data-mp="nmaqn99sq7fm">
            <a href="#/" className="text-navy-400 hover:text-white transition-colors" data-id="element-2148" data-mp="shq07p0rth68">
              Home
            </a>
            <span className="text-navy-500" data-id="element-2149" data-mp="jum17babt88r">/</span>
            <a href="#/kennisbank" className="text-navy-400 hover:text-white transition-colors" data-id="element-2150" data-mp="ex3rhiohiozp">
              Kennisbank
            </a>
            <span className="text-navy-500" data-id="element-2151" data-mp="tnkrqypdv8v0">/</span>
            <span className="text-accent-light font-medium" data-id="element-2152" data-mp="onomkw4y9bdv">
              Website checklist
            </span>
          </nav>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6 animate-fade-in-up" data-id="element-2153" data-mp="xb51zjzquxj">
            Checklist voor een succesvolle website
          </h1>
          <p className="text-lg lg:text-xl text-navy-200 max-w-2xl mx-auto mb-10 animate-fade-in-up-delay-1" data-id="element-2154" data-mp="awa6oy6c9qb1">
            Alles wat je nodig hebt voordat je een website laat maken. Van
            strategie tot livegang — met deze checklist ben je optimaal
            voorbereid.
          </p>
          <div className="animate-fade-in-up-delay-2" data-id="element-2155" data-mp="1ha9f3zx5vtj0">
            <a href="#/website-laten-maken" className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-bold px-8 py-4 rounded-button shadow-medium hover:shadow-card transition-all duration-200 text-lg" data-id="element-2156" data-mp="1outr3pndmtxl">
              Bekijk onze website diensten
              <ArrowRightIcon className="w-5 h-5" data-id="element-2157" data-mp="1jv7d3dzq59nq" />
            </a>
          </div>
        </div>
      </section>

      {/* Stap 1: Strategie */}
      <section className="py-24 bg-surface-muted" data-id="element-2158" data-mp="27luyn22kbxsr">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2159" data-mp="2ck4sn9cjwscs">
          <div className="text-center mb-16" data-id="element-2160" data-mp="2cmimxfzeq0ja">
            <span className="inline-block px-4 py-1.5 bg-accent-light text-accent font-bold text-sm rounded-full mb-4" data-id="element-2161" data-mp="1ytt2hfzj13oa">
              Stap 1
            </span>
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-2162" data-mp="22s8lz9weftwy">
              Strategie: bepaal de basis
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-2163" data-mp="2vb5rlxdo8nwc">
              Voordat je begint met bouwen, is het belangrijk om een duidelijke
              strategie te hebben.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2164" data-mp="316c8lhni896h">
            <div className="bg-white rounded-card shadow-card p-8" data-id="element-2165" data-mp="2w5y2i55gr9b3">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2166" data-mp="2w8arv3djwly6">
                <TargetIcon className="w-6 h-6 text-accent" data-id="element-2167" data-mp="2r9hbn36vqgub" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2168" data-mp="2m9bkrp8foyda">
                Doel van de website
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2169" data-mp="2dfq3nz8sr50p">
                Wat wil je bereiken? Meer aanvragen, online verkopen of
                naamsbekendheid? Een helder doel bepaalt de richting van je
                website.
              </p>
            </div>

            <div className="bg-white rounded-card shadow-card p-8" data-id="element-2170" data-mp="23gr3l523khpb">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2171" data-mp="1yjh5l10n6xpv">
                <UsersIcon className="w-6 h-6 text-accent" data-id="element-2172" data-mp="1odpn4b5geewj" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2173" data-mp="1tcsvo5kn7399">
                Doelgroep bepalen
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2174" data-mp="1ye00pwvehoc8">
                Wie zijn je ideale klanten? Hoe beter je je doelgroep kent, hoe
                effectiever je website wordt ingericht.
              </p>
            </div>

            <div className="bg-white rounded-card shadow-card p-8" data-id="element-2175" data-mp="15lwy0rti9du3">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2176" data-mp="1anvwpoeavmkp">
                <LayoutIcon className="w-6 h-6 text-accent" data-id="element-2177" data-mp="1fkmb87agia40" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2178" data-mp="1kdpamw1x0ds4">
                Belangrijkste pagina's
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2179" data-mp="mx9sy9bbwogi">
                Welke pagina's heeft je website nodig? Denk aan: homepage, over
                ons, diensten, contact en eventueel een blog.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stap 2: Content */}
      <section className="py-24 bg-white" data-id="element-2180" data-mp="223uav3xhn05t">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2181" data-mp="2g1iuy9jqe39t">
          <div className="text-center mb-16" data-id="element-2182" data-mp="2b25x6trbnbsg">
            <span className="inline-block px-4 py-1.5 bg-accent-light text-accent font-bold text-sm rounded-full mb-4" data-id="element-2183" data-mp="2b1edohb014ij">
              Stap 2
            </span>
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-2184" data-mp="2l0lpv15zyt42">
              Content: verzamel je materiaal
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-2185" data-mp="2yqffvmt8njfd">
              Goede content is de basis van een succesvolle website. Bereid dit
              voor.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2186" data-mp="38qpvfe2qx2xp">
            <div className="bg-surface-muted p-8 rounded-card border border-navy-100 flex items-start gap-6" data-id="element-2187" data-mp="33rd7eq051ptj">
              <div className="w-12 h-12 rounded-xl bg-white shadow-soft flex items-center justify-center flex-shrink-0" data-id="element-2188" data-mp="3ncdgd1grwbhm">
                <FileTextIcon className="w-6 h-6 text-accent" data-id="element-2189" data-mp="3rcx8fqmhooww" />
              </div>
              <div data-id="element-2190" data-mp="3dde4la0pnzqe">
                <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-2191" data-mp="3ddxlcnemki2u">
                  Teksten
                </h3>
                <p className="text-slate-600 leading-relaxed" data-id="element-2192" data-mp="3ifn12gcsljx9">
                  Schrijf heldere teksten die je doelgroep aanspreken. Wij
                  kunnen hierbij helpen met professionele copywriting.
                </p>
              </div>
            </div>

            <div className="bg-surface-muted p-8 rounded-card border border-navy-100 flex items-start gap-6" data-id="element-2193" data-mp="2up0vcvq9gbqf">
              <div className="w-12 h-12 rounded-xl bg-white shadow-soft flex items-center justify-center flex-shrink-0" data-id="element-2194" data-mp="2znkeyphdtbfm">
                <CameraIcon className="w-6 h-6 text-accent" data-id="element-2195" data-mp="2kruzzbam5yke" />
              </div>
              <div data-id="element-2196" data-mp="2pod54c9uvyx9">
                <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-2197" data-mp="2pqzcobwqyefh">Foto's</h3>
                <p className="text-slate-600 leading-relaxed" data-id="element-2198" data-mp="2hbndxnffcor">
                  Professionele foto's maken een groot verschil. Gebruik eigen
                  foto's of kwalitatieve stockfoto's.
                </p>
              </div>
            </div>

            <div className="bg-surface-muted p-8 rounded-card border border-navy-100 flex items-start gap-6" data-id="element-2199" data-mp="3too1g3t0ja0g">
              <div className="w-12 h-12 rounded-xl bg-white shadow-soft flex items-center justify-center flex-shrink-0" data-id="element-2200" data-mp="l94efmixqntu">
                <PaletteIcon className="w-6 h-6 text-accent" data-id="element-2201" data-mp="q75yoas3kt6w" />
              </div>
              <div data-id="element-2202" data-mp="biji65qqhve3">
                <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-2203" data-mp="bj1mlpnjvfx3">Logo</h3>
                <p className="text-slate-600 leading-relaxed" data-id="element-2204" data-mp="ghmcow938j7t">
                  Zorg dat je logo beschikbaar is in hoge kwaliteit (bij
                  voorkeur als vectorbestand).
                </p>
              </div>
            </div>

            <div className="bg-surface-muted p-8 rounded-card border border-navy-100 flex items-start gap-6" data-id="element-2205" data-mp="1911nx6ywx3rs">
              <div className="w-12 h-12 rounded-xl bg-white shadow-soft flex items-center justify-center flex-shrink-0" data-id="element-2206" data-mp="2tn15x88j9ds5">
                <TypeIcon className="w-6 h-6 text-accent" data-id="element-2207" data-mp="33exc76krwa7g" />
              </div>
              <div data-id="element-2208" data-mp="33h0641p1qnhg">
                <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-2209" data-mp="2yfkh5yjqzwts">
                  Branding
                </h3>
                <p className="text-slate-600 leading-relaxed" data-id="element-2210" data-mp="2apq8zuadiwfv">
                  Heb je een huisstijl? Denk aan kleuren, lettertypen en de
                  uitstraling die je wilt communiceren.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stap 3: Functionaliteiten */}
      <section className="py-24 bg-surface-muted" data-id="element-2211" data-mp="2fob81qdw3a2q">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2212" data-mp="3v5tpg8aqwgv9">
          <div className="text-center mb-16" data-id="element-2213" data-mp="3v87nmq0q2971">
            <span className="inline-block px-4 py-1.5 bg-accent-light text-accent font-bold text-sm rounded-full mb-4" data-id="element-2214" data-mp="3r3q3uqud3vw9">
              Stap 3
            </span>
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-2215" data-mp="2ssyl0a2n7kjn">
              Functionaliteiten: wat moet je website kunnen?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-2216" data-mp="2xsjmos976702">
              Bepaal welke functies essentieel zijn voor jouw bedrijf.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2217" data-mp="2545nwaaab6ng">
            <div className="bg-white p-8 rounded-card shadow-soft flex items-start gap-6" data-id="element-2218" data-mp="2a2gwyisr9d4m">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center flex-shrink-0" data-id="element-2219" data-mp="2a03147q93ww0">
                <MailIcon className="w-6 h-6 text-accent" data-id="element-2220" data-mp="2iuivucn6elcs" />
              </div>
              <div data-id="element-2221" data-mp="2iwlvzpi1luv8">
                <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-2222" data-mp="1md4nrixfh52b">
                  Contactformulier
                </h3>
                <p className="text-slate-600 leading-relaxed" data-id="element-2223" data-mp="1qdevb00vc3ja">
                  Een goed contactformulier verlaagt de drempel voor potentiële
                  klanten om contact op te nemen.
                </p>
              </div>
            </div>

            <div className="bg-white p-8 rounded-card shadow-soft flex items-start gap-6" data-id="element-2224" data-mp="1du1gqcwwmwjb">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center flex-shrink-0" data-id="element-2225" data-mp="19wdfyt8j69ji">
                <BookOpenIcon className="w-6 h-6 text-accent" data-id="element-2226" data-mp="zw33jaubl65e" />
              </div>
              <div data-id="element-2227" data-mp="zy5z0ae8rrhi">
                <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-2228" data-mp="v5moxnerjehg">Blog</h3>
                <p className="text-slate-600 leading-relaxed" data-id="element-2229" data-mp="v3jp6bnt66zj">
                  Een blog helpt je vindbaarheid in Google en positioneert je
                  als expert in jouw vakgebied.
                </p>
              </div>
            </div>

            <div className="bg-white p-8 rounded-card shadow-soft flex items-start gap-6" data-id="element-2230" data-mp="h5v5361kf3vj">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center flex-shrink-0" data-id="element-2231" data-mp="c68qota03hl5">
                <SearchIcon className="w-6 h-6 text-accent" data-id="element-2232" data-mp="2c9t7yqplb9zy" />
              </div>
              <div data-id="element-2233" data-mp="2h84eoskl94dy">
                <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-2234" data-mp="2h6jkj426rngm">SEO</h3>
                <p className="text-slate-600 leading-relaxed" data-id="element-2235" data-mp="22jqekpek3u13">
                  Zoekmachineoptimalisatie zorgt ervoor dat je website gevonden
                  wordt door je doelgroep.
                </p>
              </div>
            </div>

            <div className="bg-white p-8 rounded-card shadow-soft flex items-start gap-6" data-id="element-2236" data-mp="1tgz16k24eo6t">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center flex-shrink-0" data-id="element-2237" data-mp="1xlf3kbcizdha">
                <LinkIcon className="w-6 h-6 text-accent" data-id="element-2238" data-mp="1jm74digd45d3" />
              </div>
              <div data-id="element-2239" data-mp="1ojpc55in8eji">
                <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-2240" data-mp="1ol9yi7ut0r6a">
                  Integraties
                </h3>
                <p className="text-slate-600 leading-relaxed" data-id="element-2241" data-mp="3ix6vegc0mp8q">
                  Denk aan koppelingen met Google Analytics, social media,
                  boekingssystemen of CRM-software.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stap 4: Design */}
      <section className="py-24 bg-navy-900 relative overflow-hidden" data-id="element-2242" data-mp="1h0699epvvqk0">
        <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
        backgroundSize: '40px 40px'
      }} data-id="element-2243" data-mp="1c8y26pc10ha0" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2244" data-mp="1l0q94q1ud89u">
          <div className="text-center mb-16" data-id="element-2245" data-mp="1zysff809c7us">
            <span className="inline-block px-4 py-1.5 bg-white/10 text-white font-bold text-sm rounded-full mb-4 border border-white/20" data-id="element-2246" data-mp="1zwfxuvyevzi9">
              Stap 4
            </span>
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4" data-id="element-2247" data-mp="5l17ccxktw1g">
              Design: hoe ziet je website eruit?
            </h2>
            <p className="text-lg text-navy-300 max-w-2xl mx-auto" data-id="element-2248" data-mp="q9k0obhwslua">
              Een goed design combineert uitstraling met
              gebruiksvriendelijkheid.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2249" data-mp="za8cr0nqsc4j">
            <div className="bg-white/[0.06] border border-white/10 rounded-2xl p-8" data-id="element-2250" data-mp="z8x9d1y7l6dy">
              <div className="w-12 h-12 rounded-xl bg-accent flex items-center justify-center mb-6" data-id="element-2251" data-mp="14a4l1c3h2ppw">
                <MonitorIcon className="w-6 h-6 text-white" data-id="element-2252" data-mp="197wliui6nk5p" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3" data-id="element-2253" data-mp="1d8yamiidyfty">
                Gebruiksvriendelijkheid
              </h3>
              <p className="text-navy-300 leading-relaxed" data-id="element-2254" data-mp="1i19j49r6z9y4">
                Bezoekers moeten snel vinden wat ze zoeken. Een logische
                navigatie en duidelijke structuur zijn essentieel.
              </p>
            </div>

            <div className="bg-white/[0.06] border border-white/10 rounded-2xl p-8" data-id="element-2255" data-mp="1wy0d23i3jtgc">
              <div className="w-12 h-12 rounded-xl bg-accent flex items-center justify-center mb-6" data-id="element-2256" data-mp="1wvmhzunf2h8s">
                <SmartphoneIcon className="w-6 h-6 text-white" data-id="element-2257" data-mp="3hdopfyoiz80v" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3" data-id="element-2258" data-mp="3cj18qlzrjhwu">
                Responsive design
              </h3>
              <p className="text-navy-300 leading-relaxed" data-id="element-2259" data-mp="37jokbwt8u2s5">
                Je website moet er perfect uitzien op desktop, tablet én mobiel.
                Meer dan 60% van het verkeer komt via mobiel.
              </p>
            </div>

            <div className="bg-white/[0.06] border border-white/10 rounded-2xl p-8" data-id="element-2260" data-mp="3v769rlp1cory">
              <div className="w-12 h-12 rounded-xl bg-accent flex items-center justify-center mb-6" data-id="element-2261" data-mp="3qg7mg25mqqf7">
                <ZapIcon className="w-6 h-6 text-white" data-id="element-2262" data-mp="3li63ric48t49" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3" data-id="element-2263" data-mp="mrmi2r40jef0">
                Snelle laadtijd
              </h3>
              <p className="text-navy-300 leading-relaxed" data-id="element-2264" data-mp="i0e6bof7ngyo">
                Een snelle website scoort beter in Google en zorgt voor minder
                afhakers. Streef naar een laadtijd onder de 3 seconden.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stap 5: Livegang */}
      <section className="py-24 bg-white" data-id="element-2265" data-mp="3dj3s0bms4nli">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2266" data-mp="3dg7u2naxlyx2">
          <div className="text-center mb-16" data-id="element-2267" data-mp="3ihx4q1u8sakm">
            <span className="inline-block px-4 py-1.5 bg-accent-light text-accent font-bold text-sm rounded-full mb-4" data-id="element-2268" data-mp="a1c4b9foept4">
              Stap 5
            </span>
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-2269" data-mp="e2dxp9mww7n6">
              Livegang: klaar voor lancering
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-2270" data-mp="5292ryywn1pw">
              De laatste stappen voordat je website live gaat.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2271" data-mp="2ks2ai3vm6fyt">
            <div className="bg-surface-muted p-8 rounded-card border border-navy-100" data-id="element-2272" data-mp="2pthtzri58ef3">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2273" data-mp="2i85xpn3s72pj">
                <ServerIcon className="w-6 h-6 text-accent" data-id="element-2274" data-mp="2d8sz653js774" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2275" data-mp="2ra5gajug2eul">Hosting</h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2276" data-mp="2maj1i5yywqjo">
                Kies betrouwbare hosting met goede uptime en snelheid. Wij
                regelen dit volledig voor je.
              </p>
            </div>

            <div className="bg-surface-muted p-8 rounded-card border border-navy-100" data-id="element-2277" data-mp="1ujwgyzlfd559">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2278" data-mp="28ahpbud95wow">
                <ShieldCheckIcon className="w-6 h-6 text-accent" data-id="element-2279" data-mp="23kb0btetwa64" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2280" data-mp="1fugrdmxmr5r5">
                SSL-certificaat
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2281" data-mp="1avx5fmj1dtys">
                Een SSL-certificaat (https) is verplicht voor veiligheid en
                wordt beloond door Google.
              </p>
            </div>

            <div className="bg-surface-muted p-8 rounded-card border border-navy-100" data-id="element-2282" data-mp="2d5vz1i8ns5x">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2283" data-mp="h1af26l7e5p8">
                <SettingsIcon className="w-6 h-6 text-accent" data-id="element-2284" data-mp="c998906t176l" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2285" data-mp="q15uinlskmv2">
                SEO basis
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2286" data-mp="m0vhws3huy78">
                Zorg dat de technische SEO op orde is: meta-tags, sitemap,
                robots.txt en snelheidsoptimalisatie.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Gerelateerde pagina's */}
      <section className="py-16 bg-white border-b border-navy-100" data-id="element-2287" data-mp="31sauybnywl5c">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2288" data-mp="2wsetaj2qt5sy">
          <h3 className="text-lg font-semibold text-navy-900 mb-6" data-id="element-2289" data-mp="2wuhttx1iuhbx">
            Ontdek meer:
          </h3>
          <div className="flex flex-wrap justify-center gap-4" data-id="element-2290" data-mp="1h97f6ax89lz8">
            <a href="#/website-laten-maken" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-2291" data-mp="1h8ntqk8dcfgg">
              Website laten maken
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-2292" data-mp="17h1fef0qi09h">•</span>
            <a href="#/wordpress-websites" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-2293" data-mp="ti1qr7d81ldb">
              WordPress websites
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-2294" data-mp="jnjgfjdaurkj">•</span>
            <a href="#/kosten-website-laten-maken" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-2295" data-mp="olbderssx3vl">
              Kosten website laten maken
            </a>
          </div>
        </div>
      </section>

      {/* Download Checklist */}
      <div className="py-16 bg-surface-muted" data-id="element-2296" data-mp="2hip03d5rl6tw">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2297" data-mp="2chrhizguearv">
          <div className="bg-white rounded-2xl shadow-card p-8 lg:p-12 mb-16" data-id="element-2298" data-mp="27j7wr2dzj513">
            <h2 className="text-2xl font-bold text-navy-900 mb-6" data-id="element-2299" data-mp="27lak1enr6k24">
              Download de complete checklist (PDF)
            </h2>
            <p className="text-slate-600 mb-8" data-id="element-2300" data-mp="308es5si57ei4">
              Wil je deze checklist altijd bij de hand hebben? Download de
              uitgebreide PDF-versie inclusief handige templates en voorbeelden.
              Van strategie tot livegang, met deze checklist ben je optimaal
              voorbereid.
            </p>
            <a href="#/checklist-pdf" className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-bold px-8 py-4 rounded-button shadow-medium hover:shadow-card transition-all duration-200 text-lg" data-id="element-2301" data-mp="2mffg60hkk2fb">
              Download PDF
              <ArrowRightIcon className="w-5 h-5" data-id="element-2302" data-mp="10q1zskwpczcd" />
            </a>
          </div>
        </div>
      </div>

      {/* FAQ */}
      <section className="py-24 bg-surface-muted" data-id="element-2303" data-mp="15omwiacqx0w2">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2304" data-mp="19p6x5nwj2mmy">
          <div className="text-center mb-16" data-id="element-2305" data-mp="19rt0fbcdyfzd">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-2306" data-mp="1ep1lbjox4y4b">
              Veelgestelde vragen
            </h2>
          </div>

          <div className="space-y-4" data-id="element-2307" data-mp="1jls0m4swfpoo">
            {faqs.map((faq, index) => <div key={index} className="bg-white rounded-xl shadow-soft overflow-hidden" data-id="element-2308" data-mp="1oleg6kw79i0n" data-mpi={index}>
                <button onClick={() => toggleFaq(index)} className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none" data-id="element-2309" data-mp="1xf9izm9umwd4">
                  <span className="font-bold text-navy-900" data-id="element-2310" data-mp="27d780bzj8x6s">{faq.q}</span>
                  {openFaq === index ? <ChevronUpIcon className="w-5 h-5 text-accent flex-shrink-0" data-id="element-2311" data-mp="1tpyfn7n2zz1o" /> : <ChevronDownIcon className="w-5 h-5 text-navy-400 flex-shrink-0" data-id="element-2312" data-mp="1osezg26tpc73" />}
                </button>
                {openFaq === index && <div className="px-6 pb-5 text-slate-600 leading-relaxed" data-id="element-2313" data-mp="1yhq85npsseug">
                    {faq.a}
                  </div>}
              </div>)}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <FinalCtaSection data-id="element-2314" data-mp="15u2oqc09ju9e" />
    </main>;
}